/**
 * API client configuration using axios
 * Automatically attaches api/v1 prefix to all requests and handles JWT authentication
 */
import axios from 'axios';

import { refreshSessionTokens } from '../../lib/api.js';
import { env } from '../../lib/env.js';
import { notifySessionLost } from '../../lib/sessionEvents.js';
import { tokenStore } from '../../lib/tokenStore.js';

// crm-api base = the dashboard client's env.apiUrl. Relative `/api/crm/session`
// + `/api/v1/*` only reach the backend behind the web Caddy/vite-preview proxy;
// the bundled desktop webview (e.g. macOS `tauri://localhost`) has no such proxy,
// so CRM must address the absolute dashboard origin. On web env.apiUrl==='' ⇒
// identical to the old relative URL (no web change). See docs/deploy-and-build.md.
export function crmApiUrl(path: string): string {
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(path)) return path;
  return `${env.apiUrl}${path.startsWith('/') ? '' : '/'}${path}`;
}

/**
 * Re-mint the short-TTL brain crm-api session on demand.
 *
 * The CRM session token (`xop_crm_token`) is intentionally short-lived,
 * so a 401 on a CRM call usually just means "that token expired" — NOT
 * "the dashboard session is dead". We re-bootstrap it from the dashboard
 * server (which re-signs a fresh crm token off the still-valid dashboard
 * session) and retry, instead of logging the user out of the entire
 * dashboard. A shared in-flight promise collapses concurrent 401s into a
 * single re-bootstrap.
 *
 * Returns:
 *   'ok'           — fresh token stored; caller should retry.
 *   'session-dead' — the dashboard itself rejected us (401/403); the
 *                    whole session is gone → escalate to logout.
 *   'transient'    — network / 5xx; CRM is unavailable but the dashboard
 *                    session is fine → fail the call WITHOUT logging out.
 */
type RebootstrapResult = 'ok' | 'session-dead' | 'transient';
let crmRebootstrapInFlight: Promise<RebootstrapResult> | null = null;

function fetchCrmSession(): Promise<Response> {
  const access = tokenStore.getAccess();
  return fetch(crmApiUrl('/api/crm/session'), {
    headers: access ? { Authorization: `Bearer ${access}` } : {},
    credentials: 'include',
  });
}

function rebootstrapCrmSession(): Promise<RebootstrapResult> {
  if (crmRebootstrapInFlight) return crmRebootstrapInFlight;
  crmRebootstrapInFlight = (async (): Promise<RebootstrapResult> => {
    try {
      let res = await fetchCrmSession();
      // A 401 here usually means the DASHBOARD access token (30 min) has
      // lapsed — e.g. a tab reopened after a while — not that the session is
      // over. Renew it through the shared tri-state refresh and mint again;
      // only a REJECTED refresh token is 'session-dead'. Treating this 401 as
      // fatal logged people out of a session good for a year (2026-09-24,
      // docs/session-lifetime.md).
      if (res.status === 401) {
        const refreshed = await refreshSessionTokens();
        if (refreshed === 'dead') return 'session-dead';
        if (refreshed === 'transient') return 'transient';
        res = await fetchCrmSession();
      }
      if (res.status === 401 || res.status === 403) return 'session-dead';
      if (!res.ok) return 'transient';
      const s = (await res.json()) as {
        token?: string;
        user?: unknown;
        tenant?: unknown;
        permissions?: unknown;
      };
      if (!s?.token) return 'transient';
      localStorage.setItem('xop_crm_token', s.token);
      localStorage.setItem('xop_crm_user', JSON.stringify(s.user ?? {}));
      localStorage.setItem('xop_crm_tenant', JSON.stringify(s.tenant ?? {}));
      localStorage.setItem('xop_crm_permissions', JSON.stringify(s.permissions ?? []));
      return 'ok';
    } catch {
      return 'transient';
    } finally {
      crmRebootstrapInFlight = null;
    }
  })();
  return crmRebootstrapInFlight;
}

/**
 * Mint a fresh CRM session token so crm-api calls authenticate.
 *
 * `CrmSessionBootstrap` does this on `/crm` entry; a dashboard surface OUTSIDE
 * the /crm runtime (e.g. the top-nav call launcher's Contacts tab) has no token
 * and can't rely on the lazy 401→rebootstrap path firing, so it calls this
 * first. Always mints fresh (the token is short-TTL, so a cached one is usually
 * stale) and throws on failure so the caller can show an error instead of a
 * silently empty list. Concurrent callers collapse onto one in-flight mint.
 */
export async function ensureCrmSession(): Promise<void> {
  const result = await rebootstrapCrmSession();
  if (result !== 'ok') throw new Error(`crm-session-bootstrap:${result}`);
}

// Create axios instance with base configuration
const api = axios.create({
  // `env.apiUrl` is "" in the browser (same-origin, relative — works on any
  // domain) and the absolute server URL in the desktop build, where the SPA
  // loads from tauri://localhost and a relative "" base would hit the app
  // bundle instead of the server ("could not bootstrap CRM"). axios bypasses
  // the window.fetch patch, so the CRM client must set this itself.
  baseURL: typeof window === 'undefined'
    ? 'http://nginx' // Server-side: use Docker service name
    : env.apiUrl, // Client-side: same base as the dashboard client ('' on web ⇒ relative)
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add api/v1 prefix and JWT token
api.interceptors.request.use(
  (config) => {
    // Automatically add api/v1 prefix to all requests
    if (config.url && !config.url.startsWith('/api/v1')) {
      config.url = `/api/v1${config.url.startsWith('/') ? '' : '/'}${config.url}`;
    }

    // Add JWT token if available (but avoid circular dependency)
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('xop_crm_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Only log requests in verbose mode - and never log headers (Authorization)
    if (process.env.NODE_ENV === 'development' && process.env.NEXT_PUBLIC_VERBOSE_LOGGING === 'true') {
      // eslint-disable-next-line no-console
    }

    return config;
  },
  (error) => {
    // Only emit a generic error event - never log the error object (may
    // contain auth tokens / response bodies).
    // eslint-disable-next-line no-console
    console.error('api_request_error');
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Only log successful responses in verbose mode - never the body.
    if (process.env.NODE_ENV === 'development' && process.env.NEXT_PUBLIC_VERBOSE_LOGGING === 'true') {
      // eslint-disable-next-line no-console
    }
    return response;
  },
  async (error) => {
    // Check if this is a cancelled request (common in React Strict Mode)
    const isCancelled = error.code === 'ERR_NETWORK_CHANGED' ||
                       error.code === 'ERR_CANCELED' ||
                       (error.message === 'Network Error' && !error.response && !error.request?.status);

    if (isCancelled) {
      return Promise.reject(error);
    }

    // 403s are handled gracefully by components; stay quiet.
    if (error.response?.status === 403) {
      return Promise.reject(error);
    }

    // Log only the bare event. Never include response bodies, headers, or
    // request bodies - they may contain credentials or PII.
    const status = error.response?.status;
    const url = error.config?.url;
    const isStats404 = status === 404 && url?.includes('/tasks/stats');
    if (status && !isStats404) {
      // eslint-disable-next-line no-console
      console.error(`api_response_error ${status} ${error.config?.method?.toUpperCase() || ''} ${url || ''}`);
    }

    // Handle authentication errors. A CRM 401 is USUALLY just the
    // short-lived crm token expiring — re-mint it from the still-valid
    // dashboard session and retry, instead of nuking the whole login.
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      const original = error.config as (typeof error.config & { _crmRetried?: boolean }) | undefined;
      if (original && !original._crmRetried) {
        const result = await rebootstrapCrmSession();
        if (result === 'ok') {
          original._crmRetried = true;
          original.headers = original.headers ?? {};
          original.headers.Authorization = `Bearer ${localStorage.getItem('xop_crm_token')}`;
          return api(original); // retry once with the fresh token
        }
        if (result === 'session-dead') {
          // The dashboard itself rejected the re-bootstrap → the whole
          // session really is gone. THIS is the only path that logs out.
          localStorage.removeItem('xop_crm_token');
          localStorage.removeItem('xop_crm_user');
          localStorage.removeItem('xop_crm_tenant');
          notifySessionLost({ source: 'crm-api', status: 401 });
        }
        // 'transient' → CRM is unavailable but the dashboard session is
        // fine; clear the stale crm token and let the call fail without
        // bouncing the user to /login.
        else {
          localStorage.removeItem('xop_crm_token');
        }
      }
    }
    
    // Enhance error object with useful debugging info
    // Only set data if response exists (network errors don't have response)
    if (error.response) {
      if (error.response.data === null || error.response.data === undefined) {
        error.response.data = { 
          error: `Request failed with status ${error.response.status}`,
          status: error.response.status 
        };
      }
    }
    // Note: For network errors (no response), we don't mutate the error object
    // The calling code should check for error.response existence
    
    return Promise.reject(error);
  }
);

export default api;

// Stats API methods (for navigation badges)
export const statsApi = {
  // Get statistics
  getStats: () => api.get('/dashboard/stats')
};

// Financial Metrics API methods
export const financialApi = {
  // New comprehensive overview endpoint
  getOverview: (params?: { period?: string }) =>
    api.get('/financial-metrics/overview', { params }),

  getSalespersonDetails: (userId: string, params?: { period?: string }) =>
    api.get(`/financial-metrics/salesperson-details/${userId}`, { params }),

  // Leaderboard endpoint
  getLeaderboard: (params?: { period?: string; sort_by?: string }) =>
    api.get('/financial-metrics/leaderboard', { params }),

  // Product performance endpoint
  getProductPerformance: (params?: { period?: string }) =>
    api.get('/financial-metrics/product-performance', { params }),

  // Export PDF data endpoint
  exportPDF: (params?: { period?: string; user_id?: string }) =>
    api.post('/financial-metrics/export-pdf', params)
};


